import { useState, useEffect } from 'react'
import { supabase, type User, type UserRole } from '../lib/supabase'

const CONFIG = {
  maxPinLength: 8,
  minPinLength: 4,
  sessionTimeout: 5 * 60 * 1000, // 5 minuten
}

interface AuthState {
  user: User | null
  isLoading: boolean
  error: string | null
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null
  })

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🔄 Initializing auth...')
      console.log('🔄 Supabase URL:', supabase.supabaseUrl)
      
      // Add timeout to ensure this doesn't hang forever
      const timeoutId = setTimeout(() => {
        console.error('⏰ Auth initialization timeout!')
        setAuthState(prev => ({ ...prev, isLoading: false, error: 'Timeout' }))
      }, 5000)
      
      try {
        const savedPin = localStorage.getItem('pincode')
        const savedUserId = localStorage.getItem('userId')  // Real UUID from database
        const savedTimestamp = localStorage.getItem('loginTimestamp')
        const savedUserRole = localStorage.getItem('userRole')
        const savedUserName = localStorage.getItem('userName')

        console.log('💾 Saved data:', { savedPin, savedUserId, savedTimestamp, savedUserRole, savedUserName })

        if (savedPin && savedUserId && savedTimestamp && savedUserRole && savedUserName) {
          const currentTime = Date.now()
          const timeSinceLogin = currentTime - parseInt(savedTimestamp)

          console.log('⏰ Time since login:', timeSinceLogin, 'Timeout:', CONFIG.sessionTimeout)

          if (timeSinceLogin < CONFIG.sessionTimeout) {
            // Session is still valid
            const user: User = {
              id: savedUserId,  // Use real UUID, not PIN
              name: savedUserName,
              role: savedUserRole as UserRole,
              pin: savedPin,
              created_at: new Date().toISOString()
            }
            
            console.log('✅ Session valid, restoring user:', user)
            clearTimeout(timeoutId)
            setAuthState({
              user,
              isLoading: false,
              error: null
            })
            return
          } else {
            // Session expired
            console.log('⏰ Session expired, clearing data')
            clearAuthData()
          }
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error)
        clearAuthData()
        clearTimeout(timeoutId)
        setAuthState(prev => ({ ...prev, isLoading: false, error: 'Initialization error' }))
        return
      }
      
      console.log('🎯 Auth initialization complete - no saved session')
      clearTimeout(timeoutId)
      setAuthState(prev => ({ ...prev, isLoading: false }))
    }

    initializeAuth()
  }, [])

  // Check session timeout periodically
  useEffect(() => {
    const checkSessionTimeout = () => {
      const savedTimestamp = localStorage.getItem('loginTimestamp')
      
      if (savedTimestamp && authState.user) {
        const currentTime = Date.now()
        const timeSinceLogin = currentTime - parseInt(savedTimestamp)

        if (timeSinceLogin >= CONFIG.sessionTimeout) {
          logout()
        }
      }
    }

    const interval = setInterval(checkSessionTimeout, 60000) // Check every minute
    return () => clearInterval(interval)
  }, [authState.user])

  const validatePinFormat = (pin: string) => {
    return pin &&
      pin.length >= CONFIG.minPinLength &&
      pin.length <= CONFIG.maxPinLength &&
      /^\d+$/.test(pin)
  }

  const login = async (pincode: string) => {
    if (!validatePinFormat(pincode)) {
      setAuthState(prev => ({
        ...prev,
        error: "Voer een geldige pincode in (4-8 cijfers)"
      }))
      return false
    }

    setAuthState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // Test connection first
      console.log('🔍 Testing Supabase connection...')
      
      const { data, error } = await supabase
        .from('users')
        .select('id, name, role')
        .eq('pin', pincode.toString())
        .single()

      if (error || !data) {
        console.log("❌ Ongeldige pincode of fout:", error)
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: "🚫 Ongeldige pincode, probeer opnieuw."
        }))
        return false
      }

      const user: User = {
        id: data.id,  // Use the real UUID from database
        name: data.name,
        role: data.role,
        pin: pincode,
        created_at: new Date().toISOString()
      }

      // Save to localStorage (including the real user ID)
      localStorage.setItem('pincode', pincode)
      localStorage.setItem('userId', data.id)  // Store real UUID
      localStorage.setItem('userRole', data.role)
      localStorage.setItem('userName', data.name)
      localStorage.setItem('loginTimestamp', Date.now().toString())

      setAuthState({
        user,
        isLoading: false,
        error: null
      })

      console.log('✅ User login successful:', user)

      return true
    } catch (error) {
      console.error('🔥 Database fout:', error)
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: "⚠️ Er is een probleem opgetreden. Probeer het later opnieuw."
      }))
      return false
    }
  }

  const logout = () => {
    clearAuthData()
    setAuthState({
      user: null,
      isLoading: false,
      error: null
    })
  }

  const clearAuthData = () => {
    localStorage.removeItem('pincode')
    localStorage.removeItem('userId')
    localStorage.removeItem('userName')
    localStorage.removeItem('userRole')
    localStorage.removeItem('loginTimestamp')
  }

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }))
  }

  return {
    user: authState.user,
    isLoading: authState.isLoading,
    error: authState.error,
    login,
    logout,
    clearError
  }
}